import { RoomUserModel } from '@/components/chat/_domain';
import { injectable, inject } from 'inversify';
import { Lang } from 'vuejs-localization';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ILocalisationService } from '@/app-modules/localisation-service';
import { SweetAlertType } from 'sweetalert2';
import { ISweetAlertService } from '@/app-modules/sweet-alert-service';
import { ConfigurationPaths, IConfigurationService } from '@/app-modules/config';

interface IUserProfileService {
  viewUserProfile(user: RoomUserModel): Promise<void>;
}

@injectable()
class UserProfileService implements IUserProfileService {
  private readonly $lang: Lang;
  private birthdaysEnabled: boolean;
  constructor(
    @inject(SERVICE_IDENTIFIERS.ILocalisationService) private readonly localisationService: ILocalisationService,
    @inject(SERVICE_IDENTIFIERS.ISweetAlertService) private readonly sweetAlertService: ISweetAlertService,
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configurationService: IConfigurationService
  ) {
    this.$lang = this.localisationService.getLangInstance();
    this.setBirthdaysEnabled();
  }

  public async viewUserProfile(user: RoomUserModel): Promise<void> {
    const title = `${this.$lang.chat.profile.profileUsername} ${user.username}`;
    const htmlProfile = this.generateProfileMarkup(user);
    const copyUserDetails = await this.showHtmlWithConfirmModal(title, this.$lang.chat.generic.copyButton, htmlProfile);

    if (copyUserDetails) {
        this.showMessage(this.copyProfileToClipboard(user.username) ? 'success' : 'error', this.$lang.chat.profile.copyClipboardSuccess, title);
    }
  }

  private generateProfileMarkup(user: RoomUserModel): string {
    const userProfile = this.generateUserProfile(user);
    const deviceProfile = this.generateDeviceProfile(user);
    const markup = `<table class="table-profile">
                      <thead>
                        <tr class="tr-profile">
                          <th class="th-profile" colspan="2">
                            ${userProfile.map((profile) => profile.active ? profile.html : '').join('') }
                          </th>
                        </tr>
                      </thead>
                      <tbody id="CopyToClipboard">
                        ${deviceProfile.map((profile) => `<tr class="tr-profile" ><td class="td-profile" >${profile.header}</td><td class="td-profile" >${profile.info}</td></tr>`).join('') }
                      </tbody>
                    </table>`;

    return markup;
  }

  private generateUserProfile(user: RoomUserModel): { active: boolean, html: string }[] {

    return [
      { active: user.birthday && this.birthdaysEnabled, html: `<p class="fa fa-birthday-cake"> ${this.$lang.chat.profile.birthday}</p><br/>` },
      { active: user.returningPlayer, html: `<p class="fa fa-hourglass-half"> ${this.$lang.chat.profile.returningAfterLapsing}</p><br/>` },
      { active: user.newMember, html: `<p class="fa fa-star"> ${this.$lang.chat.profile.newMember}</p><br/>` },
      { active: user.notEligibleForChatPrize, html: `<p class="fa fa-money"> ${this.$lang.chat.profile.notEligibleForChatPrize.replace('{username}', user.username)}</p><br/>` },
      { active: user.chatPrize, html: `<p class="fa fa-diamond"> ${this.$lang.chat.profile.chatPrize}</p><br/>` },
      // TODO: Not returned from game engine yet { active: user.returnedFromExclusion, html: `<p class="fa fa-exclamation-circle"> ${this.$lang.chat.returnedFromExclusion}</p><br/>` },
      { active: user.twiceSinned, html: `<p class="fa fa-bell"> ${this.$lang.chat.profile.twiceSinned}</p><br/>` }
    ];
  }

  private generateDeviceProfile(user: RoomUserModel): { header: string, info: string }[] {
    const profile: { header: string, info: string }[] = [
      { header: this.$lang.chat.profile.device.game, info: `${user.gameName} - ${user.chatRoomName}` },
      { header: this.$lang.chat.profile.device.platform, info: user.readableClientType.platform },
      { header: this.$lang.chat.profile.device.platform_version, info: user.getClientVersion() },
      { header: this.$lang.chat.profile.device.os, info: user.readableClientType.os }
    ];
    if (user.readableClientType.browser) {
      profile.push({ header: this.$lang.chat.profile.device.browser, info: user.readableClientType.browser });
    }
    return profile;
  }

  private async showHtmlWithConfirmModal(messagePopupTitleText: string, confirmButtonText: string, htmlMessage: string): Promise<boolean> {
    const refocus = true;
    const {value: result} = await this.sweetAlertService.show({
      title: messagePopupTitleText,
      html: htmlMessage,
      showConfirmButton: true,
      confirmButtonText: `${confirmButtonText}`
    }, refocus);
    return result;
  }

  private showMessage(type: SweetAlertType, message: string, messagePopupTitleText: string) {
    const refocus = true;
    this.sweetAlertService.show({
      allowOutsideClick: false,
      title: messagePopupTitleText,
      text: message,
      type
    }, refocus);
  }

  private copyProfileToClipboard(username: string): boolean {
    try {
      const aux = document.createElement('textarea');
      const clipboardNodes = document.getElementById('CopyToClipboard').childNodes;
      let formattedText = `Username: ${username}\r\n`;
      Array.from(clipboardNodes).forEach((node) => {
          if (node.nodeName === 'TR') {
              formattedText = formattedText.concat(`${node.childNodes[0].textContent}: ${node.childNodes[1].textContent}\r\n`);
          }
      });
      aux.value = formattedText;
      document.body.appendChild(aux);
      aux.select();
      document.execCommand('copy');
      document.body.removeChild(aux);
      return true;
    }
    catch {
      return false;
    }
  }
  private setBirthdaysEnabled(){
    this.configurationService.getConfiguration().then((config) => {
      const birthdayConfigValue = config.getConfigurationByPath(ConfigurationPaths.features.blings.birthday);
      this.birthdaysEnabled = birthdayConfigValue === 'true' || birthdayConfigValue === undefined;
    });
  }
}

export { IUserProfileService, UserProfileService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/user-profile-service/user-profile-service.ts